import { formatDistance } from './relayPoints.js'

export const NEAR_RADIUS_KM = 50

export const FIRST_VIEW_BOUNDS = {
  south: 41,
  north: 51.6,
  west: -5.5,
  east: 10,
}

const TYPE_ICONS = {
  wrench: ['fas', 'wrench'],
  store: ['fal', 'store'],
  lightbulb: ['fas', 'lightbulb'],
  car: ['fas', 'car'],
  motorcycle: ['fas', 'motorcycle'],
  truck: ['fas', 'truck'],
  star: ['fas', 'star'],
}

const SCHEMA_TYPES = {
  installateur: 'AutoRepair',
  'renovation-de-phares': 'AutoRepair',
  revendeur: 'AutoPartsStore',
}

const DEPARTMENTS = {
  '01': 'Ain',
  '02': 'Aisne',
  '03': 'Allier',
  '04': 'Alpes-de-Haute-Provence',
  '05': 'Hautes-Alpes',
  '06': 'Alpes-Maritimes',
  '07': 'Ardèche',
  '08': 'Ardennes',
  '09': 'Ariège',
  10: 'Aube',
  11: 'Aude',
  12: 'Aveyron',
  13: 'Bouches-du-Rhône',
  14: 'Calvados',
  15: 'Cantal',
  16: 'Charente',
  17: 'Charente-Maritime',
  18: 'Cher',
  19: 'Corrèze',
  20: 'Corse',
  21: "Côte-d'Or",
  22: "Côtes-d'Armor",
  23: 'Creuse',
  24: 'Dordogne',
  25: 'Doubs',
  26: 'Drôme',
  27: 'Eure',
  28: 'Eure-et-Loir',
  29: 'Finistère',
  30: 'Gard',
  31: 'Haute-Garonne',
  32: 'Gers',
  33: 'Gironde',
  34: 'Hérault',
  35: 'Ille-et-Vilaine',
  36: 'Indre',
  37: 'Indre-et-Loire',
  38: 'Isère',
  39: 'Jura',
  40: 'Landes',
  41: 'Loir-et-Cher',
  42: 'Loire',
  43: 'Haute-Loire',
  44: 'Loire-Atlantique',
  45: 'Loiret',
  46: 'Lot',
  47: 'Lot-et-Garonne',
  48: 'Lozère',
  49: 'Maine-et-Loire',
  50: 'Manche',
  51: 'Marne',
  52: 'Haute-Marne',
  53: 'Mayenne',
  54: 'Meurthe-et-Moselle',
  55: 'Meuse',
  56: 'Morbihan',
  57: 'Moselle',
  58: 'Nièvre',
  59: 'Nord',
  60: 'Oise',
  61: 'Orne',
  62: 'Pas-de-Calais',
  63: 'Puy-de-Dôme',
  64: 'Pyrénées-Atlantiques',
  65: 'Hautes-Pyrénées',
  66: 'Pyrénées-Orientales',
  67: 'Bas-Rhin',
  68: 'Haut-Rhin',
  69: 'Rhône',
  70: 'Haute-Saône',
  71: 'Saône-et-Loire',
  72: 'Sarthe',
  73: 'Savoie',
  74: 'Haute-Savoie',
  75: 'Paris',
  76: 'Seine-Maritime',
  77: 'Seine-et-Marne',
  78: 'Yvelines',
  79: 'Deux-Sèvres',
  80: 'Somme',
  81: 'Tarn',
  82: 'Tarn-et-Garonne',
  83: 'Var',
  84: 'Vaucluse',
  85: 'Vendée',
  86: 'Vienne',
  87: 'Haute-Vienne',
  88: 'Vosges',
  89: 'Yonne',
  90: 'Territoire de Belfort',
  91: 'Essonne',
  92: 'Hauts-de-Seine',
  93: 'Seine-Saint-Denis',
  94: 'Val-de-Marne',
  95: "Val-d'Oise",
  971: 'Guadeloupe',
  972: 'Martinique',
  973: 'Guyane',
  974: 'La Réunion',
  976: 'Mayotte',
}

const FRENCH_ISOS = ['FR', 'GP', 'MQ', 'GF', 'RE', 'YT']

export function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export function cityName(city) {
  return String(city || '')
    .toLowerCase()
    .replace(/(^|[\s'-])\p{L}/gu, letter => letter.toUpperCase())
}

export function distanceKm(from, to) {
  const toRadians = value => (value * Math.PI) / 180
  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(dLng / 2) ** 2
  return 2 * 6371 * Math.asin(Math.sqrt(a))
}

export function formatKm(km, locale = 'fr') {
  return formatDistance(km * 1000, locale)
}

export function typeIcon(icon) {
  return TYPE_ICONS[icon] || ['fas', 'map-marker-alt']
}

export function websiteUrl(website) {
  const value = String(website || '').trim()
  if (!value) return null
  return /^https?:\/\//i.test(value) ? value : `https://${value}`
}

export function directionsUrl(partner) {
  return `https://www.google.com/maps/dir/?api=1&destination=${partner.lat},${partner.lng}`
}

export function withTypes(partners, types) {
  const byId = new Map(types.map(type => [type.id, type]))
  return partners.map(partner => ({
    ...partner,
    lat: Number(partner.latitude),
    lng: Number(partner.longitude),
    types: types.filter(type => (partner.type_ids || []).includes(type.id)),
    typeIds: (partner.type_ids || []).filter(id => byId.has(id)),
  }))
}

export function rankPartners(partners, { typeId = null, origin = null } = {}) {
  const filtered = typeId
    ? partners.filter(partner => partner.typeIds.includes(typeId))
    : partners
  if (!origin) {
    return [...filtered].sort(
      (a, b) => (a.position || 0) - (b.position || 0) || a.id - b.id
    )
  }
  return filtered
    .map(partner => ({ ...partner, distance: distanceKm(origin, partner) }))
    .sort((a, b) => a.distance - b.distance)
}

export function countNear(ranked, radius = NEAR_RADIUS_KM) {
  return ranked.filter(partner => partner.distance <= radius).length
}

export function countByType(partners, types) {
  const counts = { all: partners.length }
  for (const type of types) {
    counts[type.id] = partners.filter(partner =>
      partner.typeIds.includes(type.id)
    ).length
  }
  return counts
}

export function centroid(partners) {
  if (!partners.length) return null
  return {
    lat:
      partners.reduce((sum, partner) => sum + partner.lat, 0) / partners.length,
    lng:
      partners.reduce((sum, partner) => sum + partner.lng, 0) / partners.length,
  }
}

export function firstViewPartners(partners, bounds = FIRST_VIEW_BOUNDS) {
  const inside = partners.filter(
    partner =>
      partner.lat >= bounds.south &&
      partner.lat <= bounds.north &&
      partner.lng >= bounds.west &&
      partner.lng <= bounds.east
  )
  return inside.length ? inside : partners
}

export function departmentCode(postcode) {
  const value = String(postcode || '').trim()
  if (!/^\d{5}$/.test(value)) return null
  return value.startsWith('97') ? value.slice(0, 3) : value.slice(0, 2)
}

function isFrench(partner) {
  const iso = String(partner.country?.iso || 'FR').toUpperCase()
  return FRENCH_ISOS.includes(iso)
}

export function directoryGroups(partners) {
  const groups = new Map()
  for (const partner of partners) {
    const code = isFrench(partner) ? departmentCode(partner.postcode) : null
    const name = code ? DEPARTMENTS[code] || DEPARTMENTS[Number(code)] : null
    const key = name ? `fr-${code}` : `country-${partner.country?.iso || '?'}`
    const label = name ? `${name} (${code})` : partner.country?.name || '—'
    if (!groups.has(key)) {
      groups.set(key, { key, label, french: !!name, partners: [] })
    }
    groups.get(key).partners.push(partner)
  }
  return [...groups.values()].sort((a, b) => {
    if (a.french !== b.french) return a.french ? -1 : 1
    return a.key.localeCompare(b.key, 'fr', { numeric: true })
  })
}

export function locateAmongPartners(query, partners) {
  const key = normalize(query)
  if (!key) return null
  const digits = key.replace(/\s/g, '')
  if (/^\d{2,5}$/.test(digits)) {
    const matches = partners.filter(partner =>
      String(partner.postcode || '').startsWith(digits)
    )
    return matches.length ? { ...centroid(matches), label: query.trim() } : null
  }
  const exact = partners.filter(partner => normalize(partner.city) === key)
  const matches = exact.length
    ? exact
    : partners.filter(partner => normalize(partner.city).includes(key))
  return matches.length
    ? { ...centroid(matches), label: cityName(matches[0].city) }
    : null
}

export function schemaType(partner) {
  const types = partner.types.map(type => SCHEMA_TYPES[type.identifier])
  if (types.includes('AutoRepair')) return 'AutoRepair'
  if (types.includes('AutoPartsStore')) return 'AutoPartsStore'
  return 'LocalBusiness'
}
